import { ref } from 'vue'
import { showPremiumExpiredToast } from '@/composables/usePremiumPurchase'

/**
 * L'accès à Harmonia+ n'a plus qu'une source : `is_premium` / `premium_until`,
 * un ABONNEMENT écrit par les webhooks RevenueCat, qui en sont la seule
 * autorité.
 *
 * Le reverse trial (`trial_ends_at`, essai de 7 jours ouvert par le parent) a
 * été retiré le 31/08/2026 avec le passage au gating par profil : un essai
 * n'avait plus rien de neuf à ouvrir à un foyer d'un enfant, qui a déjà tout.
 * La colonne et la RPC `start_household_trial` restent en base, inutilisées,
 * et aucun foyer n'avait d'essai en cours au moment du retrait.
 */
interface PremiumStatus {
  is_premium: boolean
  premium_until: string | null
}

// Reactive tick that forces isPremium recomputation periodically
export const premiumCheckTick = ref(0)

const EXPIRED_NOTIFIED_KEY = 'harmonia_premium_expired_notified'

/** Abonnement payant actif. */
export function hasPaidPremium(status: PremiumStatus): boolean {
  void premiumCheckTick.value
  if (!status.is_premium) return false
  if (status.premium_until && new Date(status.premium_until) < new Date()) return false
  return true
}

/**
 * Accès aux fonctionnalités Harmonia+.
 *
 * Miroir de `is_household_premium()` en base, qui reste l'autorité. Celle-ci
 * teste encore `trial_ends_at` : sans conséquence, plus aucun foyer n'en a un.
 */
export function isHouseholdPremium(status: PremiumStatus): boolean {
  return hasPaidPremium(status)
}

interface ExpirableHousehold {
  id: string
  is_premium: boolean
  premium_until: string | null
}

/**
 * Detect a locally-expired subscription and surface a one-time toast.
 * The DB is NOT mutated here — the RevenueCat EXPIRATION webhook is the
 * source of truth and will flip is_premium/subscription_type server-side.
 *
 * Retourne true tant que le foyer garde l'accès Harmonia+.
 */
export async function checkPremiumExpiration(household: ExpirableHousehold): Promise<boolean> {
  if (hasPaidPremium(household)) return true

  // Abonnement arrivé à échéance : le foyer a payé un jour, on le lui dit une
  // fois. Un foyer qui n'a jamais payé n'a rien perdu, donc rien à apprendre.
  if (household.is_premium && household.premium_until) {
    if (notifyOnce(EXPIRED_NOTIFIED_KEY, household.id)) showPremiumExpiredToast()
  }

  return false
}

/**
 * true la première fois seulement pour ce foyer. Le foyer est stocké et pas un
 * simple booléen : changer de foyer doit pouvoir redéclencher le message, le
 * relancer l'app non.
 */
function notifyOnce(key: string, householdId: string): boolean {
  if (localStorage.getItem(key) === householdId) return false
  localStorage.setItem(key, householdId)
  // Bump tick so isHouseholdPremium re-evaluates and the UI hides premium features
  premiumCheckTick.value++
  return true
}
